import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";

import { useDashboardStore } from "@/stores/useDashboardStore";

import { NewsDto, NewsPayloadDto, selectableImageType } from "../..";
import InputField from "../../../../../../components/InputField/InputField";
import RichTextField from "../../../../../../components/RichTextField/RichTextField";
import { inputFieldMarginBottom } from "../../../../../../shared";
import RectangleLoader from "../../../../../../shared/components/loaders/RectangleLoader";
import useNewsFormInputs from "../../../../../../shared/hooks/useNewsFormInputs";
import ImageDropzone from "../../components/ImageDropzone/ImageDropzone";
import ReleaseNotesCheck from "../../components/ReleaseNotesCheck/ReleaseNotesCheck";
import { imageList } from "../../config";

interface EditNewsDialogContentProps {
  newsData: NewsDto | null;
  setIsPayloadReady: Dispatch<SetStateAction<boolean>>;
  setPayload: Dispatch<SetStateAction<NewsPayloadDto>>;
  isLoading: boolean;
  isSubmitLoading: boolean;
}

const EditNewsDialogContent = ({
  newsData,
  setIsPayloadReady,
  setPayload,
  isLoading,
  isSubmitLoading
}: EditNewsDialogContentProps) => {
  const { t } = useTranslation();

  const unpublishedReleaseNote = useDashboardStore(
    (state) => state.unpublishedReleaseNote
  );

  const filteredSelectedImageFromTheList: selectableImageType =
    imageList.filter(
      (image: selectableImageType) => image.imageName === newsData?.imageName
    )[0];

  const [selectedImage, setSelectedImage] =
    useState<selectableImageType | null>(filteredSelectedImageFromTheList);
  const [isInteracted, setIsInteracted] = useState<boolean>(false);
  const [isReleaseNotes, setIsReleaseNotes] = useState<boolean>(
    newsData?.releaseNotes || false
  );

  const {
    titleInputError,
    validateTitleLength,
    handleChangeTitleValue,
    title,
    setTitle,
    validateRichTextLength,
    isRichTextValid,
    handleChangeRichTextValue,
    richTextInputError,
    richTextValue,
    setRichTextValue,
    isRichTextFocused,
    handleFocus
  } = useNewsFormInputs({
    setSelectedImage,
    titleValue: newsData?.title || "",
    descriptionValue: newsData?.content || "",
    setIsInteracted
  });

  const isPayloadReadyToConfirm: boolean =
    isInteracted &&
    Boolean(title) &&
    Boolean(!titleInputError) &&
    Boolean(selectedImage) &&
    Boolean(richTextValue) &&
    isRichTextValid &&
    !richTextInputError;

  const releaseNotesImage = newsData?.releaseNotes && imageList[2];

  useEffect(() => {
    setTitle(newsData?.title || "");
    setRichTextValue(newsData?.content || "");
    newsData &&
      setSelectedImage(releaseNotesImage || filteredSelectedImageFromTheList);
    setIsReleaseNotes(newsData?.releaseNotes || false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setSelectedImage,
    newsData,
    setTitle,
    setRichTextValue,
    setIsReleaseNotes
  ]);

  useEffect(() => {
    newsData &&
      setPayload({
        id: newsData.id,
        title: title,
        content: richTextValue,
        imageName: selectedImage?.imageName as string,
        isReleaseNotes: isReleaseNotes
      });
    setIsPayloadReady(isPayloadReadyToConfirm);
  }, [
    title,
    richTextValue,
    titleInputError,
    selectedImage,
    setIsPayloadReady,
    setPayload,
    newsData,
    isPayloadReadyToConfirm,
    isReleaseNotes
  ]);

  const handleToggleCheckbox = () => {
    setIsReleaseNotes((prev) => !prev);
    setIsInteracted(true);
  };

  return (
    <>
      <ImageDropzone
        setSelectedImage={setSelectedImage}
        selectedImage={selectedImage}
        setIsInteracted={setIsInteracted}
        isLoading={isLoading}
        label={t("Dropzone##select image")}
        disabled={isSubmitLoading || isReleaseNotes}
      />
      {isLoading ? (
        <>
          <RectangleLoader
            width={"100%"}
            height={"68px"}
            customStyle={inputFieldMarginBottom}
            testId="edit-news-title-input-field-loader"
          />
          <RectangleLoader
            width={"100%"}
            height={"172px"}
            customStyle={inputFieldMarginBottom}
            testId="edit-news-description-input-field-loader"
          />
        </>
      ) : (
        <>
          <InputField
            id={uuidv7()}
            testId="edit-news-title-input-field"
            size="medium"
            fullWidth
            required
            errorText={titleInputError}
            onBlur={validateTitleLength}
            onChange={handleChangeTitleValue}
            labelLeft={t("Dialog##title")}
            placeholder={t("Dialog##enter here")}
            customStyle={inputFieldMarginBottom}
            value={title}
            disabled={isSubmitLoading || isReleaseNotes}
          />
          <RichTextField
            testId="edit-news-description-input-field"
            label={`${t("Dialog##description required")}*`}
            isRichTextFocused={isRichTextFocused}
            richTextInputError={richTextInputError}
            richTextValue={richTextValue}
            handleChangeRichTextValue={handleChangeRichTextValue}
            validateRichTextLength={validateRichTextLength}
            handleFocus={handleFocus}
            isLoading={isSubmitLoading}
            edit
          />
          <ReleaseNotesCheck
            onCheckboxChange={handleToggleCheckbox}
            isLoading={isLoading}
            unpublishedReleaseNote={unpublishedReleaseNote}
            isChecked={isReleaseNotes}
          />
        </>
      )}
    </>
  );
};

export default EditNewsDialogContent;
